import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  BookOpen,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Eye,
  FileText,
  Loader2,
  MoreVertical,
  Pencil,
  Plus,
  Search,
  Trash2,
  Upload,
  X,
  XCircle,
} from 'lucide-react'
import { TopBar } from '../components/TopBar'
import { useI18n } from '../lib/i18n'
import { authHeaders } from '../lib/api'
import type { KnowledgeCategory, KnowledgeFile } from '../lib/types'

// ─── i18n ────────────────────────────────────────────────────────────────────
const LABELS = {
  zh: {
    pageTitle: '分类知识库',
    sidebarTitle: '知识库分类',
    searchPlaceholder: '搜索分类...',
    files: '文件',
    updatedAt: '更新于',
    createdAt: '创建于',
    emptyTitle: '选择分类查看详情',
    emptyDesc: '从左侧选择一个分类，即可查看该分类下的知识文件、AI摘要及解析状态',
    createCategory: '创建分类',
    upload: '上传',
    uploadFile: '上传文件',
    chunks: '个片段',
    noFiles: '该分类下暂无文件',
    searchFiles: '搜索文件...',
    // 上传弹窗
    uploadTitle: '上传文件',
    uploadSubtitle: '支持批量上传，单个文件不超过 50MB',
    dropText: '点击或拖拽文件到此处上传',
    dropHint: '支持批量上传，单个文件不超过 50MB',
    fileType: '文件类型',
    typePrecedent: '判例文件',
    typeStatute: '法律法规',
    uploadTo: '上传至分类',
    selectCategory: '选择分类',
    cancel: '取消',
    uploadBtn: '上传',
    // 文件详情
    parsed: '已解析',
    pdf: 'PDF',
    docx: 'DOCX',
    // 重命名
    renameTitle: '重命名分类',
    nameCn: '中文名称',
    nameEn: '英文名称',
    save: '保存',
    // 上传进度
    processing: '处理中',
    elapsed: (s: number) => `已等待 ${s}s`,
    timeoutHint: '文件较大或含扫描图片，正在 OCR 识别，请耐心等待…',
    // 删除文件
    selectAll: '全选',
    deselectAll: '取消全选',
    deleteSelected: '删除选中',
    deleteConfirm: '确定要删除选中的 {n} 个文件吗？此操作不可恢复。',
    deleted: '已删除 {n} 个文件',
    deleteMode: '管理文件',
    exitDeleteMode: '完成',
    downloadFile: '下载文件',
    previewFile: '预览文件',
    moreActions: '更多操作',
    court: '法院',
    decisionDate: '决定日期',
    citation: '引用',
    pending: '等待处理',
    processingStatus: '处理中',
    failed: '处理失败',
    failedPrefix: '处理失败: ',
    loading: '加载中...',
    parsedChunks: '已解析 · {n} 个片段',
    fileNotAvailable: '文件不可用',
  },
  en: {
    pageTitle: 'Knowledge Base',
    sidebarTitle: 'Categories',
    searchPlaceholder: 'Search categories...',
    files: 'files',
    updatedAt: 'Updated',
    createdAt: 'Created',
    emptyTitle: 'Select a category to view details',
    emptyDesc: 'Choose a category from the left to view knowledge files, AI summaries and parsing status',
    createCategory: 'Create category',
    upload: 'Upload',
    uploadFile: 'Upload File',
    chunks: 'chunks',
    noFiles: 'No files in this category',
    searchFiles: 'Search files...',
    uploadTitle: 'Upload File',
    uploadSubtitle: 'Batch upload supported, max 50MB per file',
    dropText: 'Click or drag files here to upload',
    dropHint: 'Batch upload supported, max 50MB per file',
    fileType: 'File Type',
    typePrecedent: 'Case Files',
    typeStatute: 'Legal Statutes',
    uploadTo: 'Upload to Category',
    selectCategory: 'Select category',
    cancel: 'Cancel',
    uploadBtn: 'Upload',
    parsed: 'Parsed',
    pdf: 'PDF',
    docx: 'DOCX',
    renameTitle: 'Rename Category',
    nameCn: 'Chinese Name',
    nameEn: 'English Name',
    processing: 'Processing',
    elapsed: (s: number) => `${s}s elapsed`,
    timeoutHint: 'Large file or scanned PDF — OCR in progress, please wait…',
    save: 'Save',
    selectAll: 'Select All',
    deselectAll: 'Deselect All',
    deleteSelected: 'Delete Selected',
    deleteConfirm: 'Delete {n} selected file(s)? This cannot be undone.',
    deleted: '{n} file(s) deleted',
    deleteMode: 'Manage Files',
    exitDeleteMode: 'Done',
    downloadFile: 'Download',
    previewFile: 'Preview',
    moreActions: 'More actions',
    court: 'Court',
    decisionDate: 'Date',
    citation: 'Citation',
    pending: 'Pending',
    processingStatus: 'Processing',
    failed: 'Failed',
    failedPrefix: 'Failed: ',
    loading: 'Loading...',
    parsedChunks: 'Parsed · {n} chunks',
    fileNotAvailable: 'File not available',
  },
}

export function KnowledgeBase() {
  const { lang } = useI18n()
  const t = LABELS[lang] ?? LABELS.zh
  const [searchParams] = useSearchParams()
  const catParam = searchParams.get('cat')

  const [categories, setCategories] = useState<KnowledgeCategory[]>([])
  const [selectedKey, setSelectedKey] = useState<string | null>(catParam)
  const [searchTerm, setSearchTerm] = useState('')
  const [files, setFiles] = useState<KnowledgeFile[]>([])
  const [filesTotal, setFilesTotal] = useState(0)
  const [loadingFiles, setLoadingFiles] = useState(false)
  const [showUpload, setShowUpload] = useState(false)
  const [showCreateCat, setShowCreateCat] = useState(false)
  const [createCatParent, setCreateCatParent] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set())

  // 加载分类列表
  useEffect(() => {
    fetch('/knowledge/categories')
      .then((r) => r.json())
      .then((data: { categories?: KnowledgeCategory[] }) => {
        if (data.categories) setCategories(data.categories)
      })
      .catch(() => {})
  }, [refreshKey])

  // 选中分类时加载文件列表
  const loadFiles = useCallback(() => {
    if (!selectedKey) {
      setFiles([])
      setFilesTotal(0)
      return
    }
    setLoadingFiles(true)
    fetch(`/knowledge/categories/${selectedKey}/files?limit=50`)
      .then((r) => r.json())
      .then((data: { files?: KnowledgeFile[]; total?: number }) => {
        setFiles(data.files ?? [])
        setFilesTotal(data.total ?? 0)
      })
      .catch(() => {})
      .finally(() => setLoadingFiles(false))
  }, [selectedKey])

  useEffect(() => { loadFiles() }, [loadFiles])

  // Auto-refresh when files are processing
  useEffect(() => {
    const hasProcessing = files.some(f => 
      f.processing_status === 'pending' || f.processing_status === 'processing'
    )
    
    if (hasProcessing) {
      const interval = setInterval(() => {
        loadFiles()
      }, 3000) // Refresh every 3 seconds
      
      return () => clearInterval(interval)
    }
  }, [files, loadFiles])

  // Helper: find a category by key in the tree (checks children too)
  const findCatByKey = (cats: KnowledgeCategory[], key: string): KnowledgeCategory | undefined => {
    for (const c of cats) {
      if (c.key === key) return c
      if (c.children?.length) {
        const found = findCatByKey(c.children, key)
        if (found) return found
      }
    }
    return undefined
  }

  // 过滤分类 (searches parent names and child names)
  const filteredCategories = searchTerm
    ? categories.filter(
        (c) =>
          c.name_zh.includes(searchTerm) ||
          c.name_en.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.children?.some(
            (ch) =>
              ch.name_zh.includes(searchTerm) ||
              ch.name_en.toLowerCase().includes(searchTerm.toLowerCase()),
          ),
      )
    : categories

  const selectedCat = findCatByKey(categories, selectedKey ?? '')

  const toggleExpanded = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  return (
    <>
      <TopBar title={t.pageTitle} />
      <div className="flex flex-1 min-h-0">
        {/* 左侧分类列表 */}
        <div className="w-72 border-r border-brand-200 bg-white flex flex-col flex-shrink-0">
          {/* 标题 + 添加按钮 */}
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <h3 className="text-sm font-semibold text-brand-800">
              {t.sidebarTitle}
            </h3>
            <button
              type="button"
              onClick={() => setShowCreateCat(true)}
              className="w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-colors"
              title={t.createCategory}
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {/* 搜索框 */}
          <div className="px-4 pb-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t.searchPlaceholder}
                className="w-full pl-8 pr-3 py-2 rounded-lg border border-brand-200 text-sm text-brand-700 placeholder:text-brand-400 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
              />
            </div>
          </div>

          {/* 分类列表 */}
          <div className="flex-1 overflow-y-auto px-2 pb-3">
            {filteredCategories.map((cat) => {
              const isActive = cat.key === selectedKey
              const name = lang === 'zh' ? cat.name_zh : cat.name_en
              const desc = lang === 'zh' ? cat.desc_zh : cat.desc_en
              const dateStr = cat.last_updated
                ? cat.last_updated.split('T')[0] ?? cat.last_updated.slice(0, 10)
                : null
              const isCustom = cat.key.startsWith('custom_')
              const hasChildren = cat.children && cat.children.length > 0
              const isExpanded = expandedKeys.has(cat.key)

              return (
                <div key={cat.key} className="mb-1">
                  {/* Parent category row */}
                  <div
                    className={`relative group w-full text-left px-3 py-3 rounded-lg transition-colors cursor-pointer ${
                      isActive
                        ? 'bg-blue-50 border border-blue-200'
                        : 'hover:bg-brand-50 border border-transparent'
                    }`}
                    onClick={() => setSelectedKey(cat.key)}
                  >
                    <div className="flex items-start gap-2.5">
                      {hasChildren ? (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleExpanded(cat.key) }}
                          className="mt-0.5 flex-shrink-0 p-0.5 rounded hover:bg-brand-200/50 transition-colors"
                        >
                          {isExpanded
                            ? <ChevronDown className={`w-4 h-4 ${isActive ? 'text-blue-600' : 'text-brand-400'}`} />
                            : <ChevronRight className={`w-4 h-4 ${isActive ? 'text-blue-600' : 'text-brand-400'}`} />
                          }
                        </button>
                      ) : (
                        <BookOpen
                          className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                            isActive ? 'text-blue-600' : 'text-brand-400'
                          }`}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div
                          className={`text-sm font-medium truncate ${
                            isActive ? 'text-blue-800' : 'text-brand-800'
                          }`}
                        >
                          {name}
                        </div>
                        <p className="text-xs text-brand-400 mt-0.5 line-clamp-2 leading-snug">
                          {desc}
                        </p>
                        <div className="flex items-center gap-2 mt-1.5 text-[11px] text-brand-400">
                          <span>
                            {cat.file_count} {t.files}
                          </span>
                          {dateStr && (
                            <>
                              <span>·</span>
                              <span>
                                {t.updatedAt} {dateStr}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    {isCustom && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          const msg = lang === 'zh'
                            ? `确定要删除分类「${name}」吗？此操作不可恢复。`
                            : `Delete category "${name}"? This cannot be undone.`
                          if (!window.confirm(msg)) return
                          fetch(`/knowledge/categories/${cat.key}`, { method: 'DELETE' })
                            .then((r) => {
                              if (r.ok) {
                                if (selectedKey === cat.key) setSelectedKey(null)
                                setRefreshKey((k) => k + 1)
                              }
                            })
                        }}
                        className="absolute top-2 right-2 p-1 rounded-md text-brand-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
                        title={lang === 'zh' ? '删除分类' : 'Delete category'}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>

                  {/* Subcategories (children) */}
                  {hasChildren && isExpanded && (
                    <div className="ml-4 mt-0.5 border-l-2 border-brand-100 pl-2">
                      {cat.children.map((child) => {
                        const childActive = child.key === selectedKey
                        const childName = lang === 'zh' ? child.name_zh : child.name_en
                        const childCustom = child.key.startsWith('custom_')
                        return (
                          <div
                            key={child.key}
                            className={`relative group w-full text-left px-3 py-2 rounded-lg mb-0.5 transition-colors cursor-pointer ${
                              childActive
                                ? 'bg-blue-50 border border-blue-200'
                                : 'hover:bg-brand-50 border border-transparent'
                            }`}
                            onClick={() => setSelectedKey(child.key)}
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                  childActive ? 'bg-blue-500' : 'bg-brand-300'
                                }`}
                              />
                              <div className="flex-1 min-w-0">
                                <div
                                  className={`text-xs font-medium truncate ${
                                    childActive ? 'text-blue-700' : 'text-brand-700'
                                  }`}
                                >
                                  {childName}
                                </div>
                                <span className="text-[10px] text-brand-400">
                                  {child.file_count} {t.files}
                                </span>
                              </div>
                            </div>
                            {childCustom && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  const msg = lang === 'zh'
                                    ? `确定要删除子分类「${childName}」吗？`
                                    : `Delete subcategory "${childName}"?`
                                  if (!window.confirm(msg)) return
                                  fetch(`/knowledge/categories/${child.key}`, { method: 'DELETE' })
                                    .then((r) => {
                                      if (r.ok) {
                                        if (selectedKey === child.key) setSelectedKey(null)
                                        setRefreshKey((k) => k + 1)
                                      }
                                    })
                                }}
                                className="absolute top-1.5 right-1.5 p-0.5 rounded text-brand-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
                                title={lang === 'zh' ? '删除子分类' : 'Delete subcategory'}
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        )
                      })}
                      {/* Add subcategory button */}
                      <button
                        type="button"
                        onClick={() => {
                          setShowCreateCat(true)
                          setCreateCatParent(cat.key)
                        }}
                        className="w-full text-left px-3 py-1.5 rounded-lg text-[11px] text-brand-400 hover:text-blue-600 hover:bg-blue-50/50 transition-colors flex items-center gap-1.5"
                      >
                        <Plus className="w-3 h-3" />
                        {lang === 'zh' ? '添加子分类' : 'Add subcategory'}
                      </button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* 右侧内容区 */}
        <div className="flex-1 flex flex-col min-w-0 bg-brand-50">
          {selectedKey && selectedCat ? (
            <CategoryDetail
              cat={selectedCat}
              files={files}
              total={filesTotal}
              loading={loadingFiles}
              lang={lang}
              t={t}
              onUpload={() => setShowUpload(true)}
              onRenamed={() => setRefreshKey((k) => k + 1)}
              onFilesDeleted={() => { loadFiles(); setRefreshKey((k) => k + 1) }}
              onRefreshFiles={loadFiles}
            />
          ) : (
            <EmptyState t={t} onUpload={() => setShowUpload(true)} onCreateCat={() => setShowCreateCat(true)} />
          )}
        </div>
      </div>

      {/* 上传弹窗 */}
      {showUpload && (
        <UploadModal
          t={t}
          categories={categories}
          lang={lang}
          selectedKey={selectedKey}
          onClose={() => setShowUpload(false)}
          onUploaded={() => { loadFiles(); setRefreshKey((k) => k + 1) }}
        />
      )}

      {/* 创建分类弹窗 */}
      {showCreateCat && (
        <CreateCategoryModal
          lang={lang}
          parentKey={createCatParent}
          categories={categories}
          onClose={() => { setShowCreateCat(false); setCreateCatParent(null) }}
          onCreated={(newKey) => {
            setRefreshKey((k) => k + 1)
            setSelectedKey(newKey)
            if (createCatParent) setExpandedKeys((prev) => new Set(prev).add(createCatParent))
            setShowCreateCat(false)
            setCreateCatParent(null)
          }}
        />
      )}
    </>
  )
}

// ─── 空状态 ──────────────────────────────────────────────────────────────────
function EmptyState({ t, onUpload, onCreateCat }: { t: (typeof LABELS)['zh']; onUpload: () => void; onCreateCat: () => void }) {
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center max-w-sm">
        <div className="w-16 h-16 rounded-2xl bg-brand-100 flex items-center justify-center mx-auto mb-4">
          <BookOpen className="w-8 h-8 text-brand-400" />
        </div>
        <h3 className="text-lg font-semibold text-brand-700 mb-2">
          {t.emptyTitle}
        </h3>
        <p className="text-sm text-brand-400 leading-relaxed mb-6">
          {t.emptyDesc}
        </p>
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={onCreateCat}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t.createCategory}
          </button>
          <button
            type="button"
            onClick={onUpload}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-brand-300 text-brand-600 text-sm font-medium hover:bg-brand-100 transition-colors"
          >
            <Upload className="w-4 h-4" />
            {t.upload}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── 分类详情 ────────────────────────────────────────────────────────────────
function CategoryDetail({
  cat,
  files,
  total,
  loading,
  lang,
  t,
  onUpload,
  onRenamed,
  onFilesDeleted,
  onRefreshFiles,
}: {
  cat: KnowledgeCategory
  files: KnowledgeFile[]
  total: number
  loading: boolean
  lang: string
  t: (typeof LABELS)['zh']
  onUpload: () => void
  onRenamed: () => void
  onFilesDeleted: () => void
  onRefreshFiles: () => void
}) {
  const name = lang === 'zh' ? cat.name_zh : cat.name_en
  const desc = lang === 'zh' ? cat.desc_zh : cat.desc_en

  // Rename modal state
  const [showRename, setShowRename] = useState(false)
  const [renameZh, setRenameZh] = useState(cat.name_zh)
  const [renameEn, setRenameEn] = useState(cat.name_en)
  const [renameSaving, setRenameSaving] = useState(false)

  // Delete mode state
  const [deleteMode, setDeleteMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  // Preview modal state
  const [previewFile, setPreviewFile] = useState<KnowledgeFile | null>(null)
  const [showPreview, setShowPreview] = useState(false)

  // Auto-refresh when files are processing
  useEffect(() => {
    const hasProcessing = files.some(f => 
      f.processing_status === 'pending' || f.processing_status === 'processing'
    )
    
    if (hasProcessing) {
      const interval = setInterval(() => {
        onRefreshFiles()
      }, 3000) // Refresh every 3 seconds
      
      return () => clearInterval(interval)
    }
  }, [files, onRefreshFiles])

  // Reset delete mode when category changes
  useEffect(() => {
    setDeleteMode(false)
    setSelectedIds(new Set())
  }, [cat.key])

  const handlePreview = (file: KnowledgeFile) => {
    setPreviewFile(file)
    setShowPreview(true)
  }

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectAll = () => {
    if (selectedIds.size === files.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(files.map((f) => f.precedent_id)))
    }
  }

  const handleRename = () => {
    setRenameSaving(true)
    fetch(`/knowledge/categories/${cat.key}`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ name_zh: renameZh, name_en: renameEn }),
    })
      .then((r) => {
        if (r.ok) {
          setShowRename(false)
          onRenamed()
        }
      })
      .catch(() => {})
      .finally(() => setRenameSaving(false))
  }

  const handleDeleteSelected = () => {
    if (selectedIds.size === 0) return
    const msg = t.deleteConfirm.replace('{n}', String(selectedIds.size))
    if (!window.confirm(msg)) return

    fetch('/knowledge/files', {
      method: 'DELETE',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ precedent_ids: Array.from(selectedIds) }),
    })
      .then(async (r) => {
        if (r.ok) {
          const data = await r.json().catch(() => ({}))
          const n = data.deleted ?? selectedIds.size
          setSelectedIds(new Set())
          setDeleteMode(false)
          onFilesDeleted()
          alert(t.deleted.replace('{n}', String(n)))
        } else {
          alert(lang === 'zh' ? '删除失败，请重试' : 'Delete failed, please try again')
        }
      })
      .catch(() => {
        alert(lang === 'zh' ? '网络错误，删除失败' : 'Network error, delete failed')
      })
  }

  return (
    <div className="flex-1 flex flex-col p-6 overflow-y-auto">
      {/* 分类头部 */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-brand-800">{name}</h2>
          <p className="text-sm text-brand-400 mt-0.5">{desc}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setRenameZh(cat.name_zh)
              setRenameEn(cat.name_en)
              setShowRename(true)
            }}
            className="p-2 rounded-lg text-brand-400 hover:bg-brand-100 hover:text-brand-600 transition-colors"
            title={t.renameTitle}
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setDeleteMode(!deleteMode)}
            className={`p-2 rounded-lg transition-colors ${
              deleteMode
                ? 'bg-red-50 text-red-500'
                : 'text-brand-400 hover:bg-red-50 hover:text-red-500'
            }`}
            title={t.deleteMode}
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onUpload}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            <Upload className="w-4 h-4" />
            {t.uploadFile}
          </button>
        </div>
      </div>

      {/* 统计栏 */}
      <div className="flex items-center gap-3 mb-5 text-xs text-brand-400">
        {cat.last_updated && (
          <>
            <span>{t.updatedAt} {cat.last_updated.split('T')[0]}</span>
            <span>·</span>
          </>
        )}
        <span>{total} {t.files}</span>
      </div>

      {/* 删除模式工具栏 */}
      {deleteMode && files.length > 0 && (
        <div className="flex items-center gap-3 mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
          <button
            type="button"
            onClick={selectAll}
            className="text-xs font-medium text-red-600 hover:text-red-700"
          >
            {selectedIds.size === files.length ? t.deselectAll : t.selectAll}
          </button>
          <span className="text-xs text-red-400">
            {selectedIds.size} / {files.length}
          </span>
          <div className="flex-1" />
          <button
            type="button"
            onClick={handleDeleteSelected}
            disabled={selectedIds.size === 0}
            className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-red-600 text-white text-xs font-medium hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Trash2 className="w-3 h-3" />
            {t.deleteSelected} ({selectedIds.size})
          </button>
          <button
            type="button"
            onClick={() => { setDeleteMode(false); setSelectedIds(new Set()) }}
            className="text-xs font-medium text-brand-500 hover:text-brand-700"
          >
            {t.exitDeleteMode}
          </button>
        </div>
      )}

      {/* 文件列表 */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-sm text-brand-400">Loading...</div>
        </div>
      ) : files.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-sm text-brand-400">{t.noFiles}</div>
        </div>
      ) : (
        <div className="space-y-3">
          {files.map((file) => (
            <FileCard
              key={file.precedent_id}
              file={file}
              t={t}
              deleteMode={deleteMode}
              selected={selectedIds.has(file.precedent_id)}
              onToggle={() => toggleSelect(file.precedent_id)}
              onPreview={handlePreview}
            />
          ))}
        </div>
      )}

      {/* 文件预览模态框 */}
      {showPreview && previewFile && (
        <FilePreviewModal
          file={previewFile}
          onClose={() => {
            setShowPreview(false)
            setPreviewFile(null)
          }}
          lang={lang}
          t={t}
        />
      )}

      {/* 重命名弹窗 */}
      {showRename && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowRename(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 mx-4">
            <button
              type="button"
              onClick={() => setShowRename(false)}
              className="absolute top-4 right-4 p-1 rounded-lg text-brand-400 hover:bg-brand-100 hover:text-brand-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-semibold text-brand-800 mb-4">{t.renameTitle}</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-brand-600 mb-1">{t.nameCn}</label>
                <input
                  type="text"
                  value={renameZh}
                  onChange={(e) => setRenameZh(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-brand-200 text-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                />
              </div>
              <div>
                <label className="block text-sm text-brand-600 mb-1">{t.nameEn}</label>
                <input
                  type="text"
                  value={renameEn}
                  onChange={(e) => setRenameEn(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-brand-200 text-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => setShowRename(false)}
                className="px-4 py-2 rounded-lg border border-brand-200 text-sm text-brand-600 hover:bg-brand-50 transition-colors"
              >
                {t.cancel}
              </button>
              <button
                type="button"
                onClick={handleRename}
                disabled={renameSaving || (!renameZh.trim() && !renameEn.trim())}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {t.save}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── 文件卡片 ────────────────────────────────────────────────────────────────
function FileCard({
  file,
  t,
  deleteMode,
  selected,
  onToggle,
  onPreview,
}: {
  file: KnowledgeFile
  t: (typeof LABELS)['zh']
  deleteMode?: boolean
  selected?: boolean
  onToggle?: () => void
  onPreview?: (file: KnowledgeFile) => void
}) {
  const hasStatuteTag = file.categories.some(
    (c) => c.includes('法令') || c.includes('法规') || c.includes('legislation') || c.includes('statute')
  )
  const typeTag = hasStatuteTag ? t.typeStatute : t.typePrecedent
  const typeColor = hasStatuteTag
    ? 'bg-blue-100 text-blue-700'
    : 'bg-amber-100 text-amber-700'
  const iconColor = hasStatuteTag ? 'text-blue-500' : 'text-red-400'

  const [showMenu, setShowMenu] = useState(false)

  // 根据处理状态决定显示内容
  const isProcessing = file.processing_status === 'pending' || file.processing_status === 'processing'
  const isFailed = file.processing_status === 'failed'
  const isDone = file.processing_status === 'done' || !file.processing_status

  const handleDownload = () => {
    fetch(`/knowledge/user-knowledge/${file.precedent_id}/file`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.blob()
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = file.case_name || file.precedent_id
        a.click()
        URL.revokeObjectURL(url)
      })
      .catch(() => {})
    setShowMenu(false)
  }

  return (
    <div
      className={`bg-white rounded-xl border p-4 transition-all ${
        deleteMode && selected
          ? 'border-red-300 bg-red-50/30 shadow-sm'
          : 'border-brand-200 hover:border-blue-200 hover:shadow-sm'
      } ${deleteMode ? 'cursor-pointer' : ''}`}
      onClick={deleteMode ? onToggle : undefined}
    >
      <div className="flex items-start gap-3">
        {deleteMode && (
          <div className="mt-0.5 flex-shrink-0">
            <div className={`w-4.5 h-4.5 rounded border-2 flex items-center justify-center transition-colors ${
              selected ? 'bg-red-500 border-red-500' : 'border-brand-300'
            }`}>
              {selected && <Check className="w-3 h-3 text-white" />}
            </div>
          </div>
        )}
        <div className="mt-0.5 flex-shrink-0">
          <FileText className={`w-5 h-5 ${iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-semibold text-brand-800">
              {file.case_name ?? file.precedent_id}
            </span>
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${typeColor}`}>
              {typeTag}
            </span>
            {isProcessing ? (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-blue-50 text-blue-600 font-medium">
                <Loader2 className="w-3 h-3 animate-spin" />
                {file.processing_status === 'pending' ? t.pending : t.processingStatus}
              </span>
            ) : isFailed ? (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-red-50 text-red-600 font-medium">
                ✗ {t.failed}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-emerald-50 text-emerald-600 font-medium">
                ✓ {t.parsed}
              </span>
            )}
          </div>

          {/* 详细信息展示 */}
          <div className="space-y-1 mb-1.5 text-xs">
            {file.court && (
              <div className="flex items-start gap-2">
                <span className="text-brand-400 min-w-[60px]">{t.court}：</span>
                <span className="text-brand-600 flex-1">{file.court}</span>
              </div>
            )}
            {file.decision_date && (
              <div className="flex items-start gap-2">
                <span className="text-brand-400 min-w-[60px]">{t.decisionDate}：</span>
                <span className="text-brand-600 flex-1">{file.decision_date}</span>
              </div>
            )}
            {file.citation && (
              <div className="flex items-start gap-2">
                <span className="text-brand-400 min-w-[60px]">{t.citation}：</span>
                <span className="text-brand-600 flex-1 line-clamp-1">{file.citation}</span>
              </div>
            )}
          </div>

          {isFailed && file.processing_error && (
            <p className="text-xs text-red-500 mt-1">
              {t.failedPrefix}{file.processing_error}
            </p>
          )}
        </div>
        {!deleteMode && isDone && (
          <div className="flex items-center gap-1 flex-shrink-0">
            {isDone && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    onPreview?.(file)
                  }}
                  className="p-1.5 rounded text-brand-400 hover:bg-brand-100 hover:text-brand-600 transition-colors"
                  title={t.previewFile}
                >
                  <Eye className="w-4 h-4" />
                </button>
                <div className="relative">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowMenu(!showMenu)
                    }}
                    className="p-1.5 rounded text-brand-400 hover:bg-brand-100 hover:text-brand-600 transition-colors"
                    title={t.moreActions}
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>
                  {showMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                      <div className="absolute right-0 top-8 z-20 bg-white rounded-lg shadow-lg border border-brand-200 py-1 min-w-[120px]">
                        <button
                          type="button"
                          onClick={handleDownload}
                          className="w-full px-3 py-2 text-left text-sm text-brand-700 hover:bg-brand-50 transition-colors"
                        >
                          {t.downloadFile}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}


// ─── 文件预览模态框 ──────────────────────────────────────────────────────────
function FilePreviewModal({
  file,
  onClose,
  lang,
  t,
}: {
  file: KnowledgeFile
  onClose: () => void
  lang: string
  t: (typeof LABELS)['zh']
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState<'file' | 'text'>('file')
  const [extractedText, setExtractedText] = useState<string>('')

  const fileId = file.precedent_id
  const fileName = file.case_name || fileId

  useEffect(() => {
    let objectUrl: string | null = null
    setLoading(true)

    // 获取提取的文本
    fetch(`/precedent-content?id=${encodeURIComponent(fileId)}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then((data: { full_text?: string }) => {
        setExtractedText(data.full_text || '')
      })
      .catch(() => {
        setExtractedText('')
      })

    // 获取原始文件用于预览
    fetch(`/knowledge/user-knowledge/${fileId}/view`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.blob()
      })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob)
        setBlobUrl(objectUrl)
      })
      .catch(() => {
        setBlobUrl(null)
      })
      .finally(() => setLoading(false))

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [fileId])

  const handleDownload = () => {
    fetch(`/knowledge/user-knowledge/${fileId}/file`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.blob()
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = fileName
        a.click()
        URL.revokeObjectURL(url)
      })
      .catch(() => {})
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] mx-4 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-brand-200 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-brand-800 truncate">{fileName}</h3>
            <div className="flex items-center gap-3 mt-1 text-xs text-brand-400">
              {file.n_chunks > 0 && (
                <span className="text-green-600">
                  {t.parsedChunks.replace('{n}', String(file.n_chunks))}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* 视图切换按钮 */}
            {blobUrl && extractedText && (
              <div className="flex items-center gap-1 px-2 py-1 bg-brand-100 rounded-lg">
                <button
                  type="button"
                  onClick={() => setViewMode('file')}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    viewMode === 'file'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-brand-600 hover:text-brand-800'
                  }`}
                >
                  {lang === 'zh' ? '原文件' : 'Original'}
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('text')}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    viewMode === 'text'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-brand-600 hover:text-brand-800'
                  }`}
                >
                  {lang === 'zh' ? '提取文本' : 'Extracted'}
                </button>
              </div>
            )}
            <button
              type="button"
              onClick={handleDownload}
              className="p-2 rounded-lg text-brand-400 hover:bg-brand-100 hover:text-brand-600 transition-colors"
              title={lang === 'zh' ? '下载' : 'Download'}
            >
              <FileText className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg text-brand-400 hover:bg-brand-100 hover:text-brand-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-sm text-brand-400">
              {t.loading}
            </div>
          ) : viewMode === 'file' && blobUrl ? (
            <iframe
              src={blobUrl}
              className="w-full h-full min-h-[70vh]"
              title={fileName}
            />
          ) : viewMode === 'text' && extractedText ? (
            <div className="p-6">
              <div className="text-sm text-brand-700 leading-relaxed whitespace-pre-wrap font-mono bg-brand-50 rounded-xl p-5">
                {extractedText}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-20 text-sm text-brand-400">
              {lang === 'zh' ? t.fileNotAvailable : 'File not available'}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── 上传弹窗 ────────────────────────────────────────────────────────────────

interface FileUploadItem {
  file: File
  status: 'pending' | 'uploading' | 'done' | 'error'
  error?: string
  precedentId?: string
  nChunks?: number
}

function UploadModal({
  t,
  categories,
  lang,
  selectedKey,
  onClose,
  onUploaded,
}: {
  t: (typeof LABELS)['zh']
  categories: KnowledgeCategory[]
  lang: string
  selectedKey: string | null
  onClose: () => void
  onUploaded: () => void
}) {
  const [targetCategory, setTargetCategory] = useState(selectedKey ?? '')
  const [files, setFiles] = useState<FileUploadItem[]>([])
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [uploadingIdx, setUploadingIdx] = useState<number | null>(null)
  const [elapsed, setElapsed] = useState(0)
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const startElapsedTimer = () => {
    setElapsed(0)
    if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current)
    elapsedTimerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000)
  }
  const stopElapsedTimer = () => {
    if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null }
    setElapsed(0)
  }

  // Flatten categories into a flat list (parent + children)
  const flatCats = categories.flatMap((cat) =>
    cat.children?.length
      ? [cat, ...cat.children.map((c) => ({ ...c, name_zh: `  └ ${c.name_zh}`, name_en: `  └ ${c.name_en}` }))]
      : [cat]
  )

  const addFiles = (fileList: FileList | File[]) => {
    const newItems: FileUploadItem[] = Array.from(fileList).map((f) => ({
      file: f,
      status: 'pending' as const,
    }))
    setFiles((prev) => [...prev, ...newItems])
  }

  const removeFile = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx))
  }

  const handleUpload = async () => {
    if (!targetCategory) return
    if (files.length === 0) return

    setUploading(true)
    let anySuccess = false

    for (let i = 0; i < files.length; i++) {
      const item = files[i]
      if (item.status === 'done') continue

      setUploadingIdx(i)
      setFiles((prev) => prev.map((f, idx) => idx === i ? { ...f, status: 'uploading' } : f))
      startElapsedTimer()

      const form = new FormData()
      form.append('file', item.file, item.file.name)

      const ctrl = new AbortController()
      const timeoutId = setTimeout(() => ctrl.abort(), 600_000)

      try {
        const res = await fetch(
          `/knowledge/upload?category_key=${encodeURIComponent(targetCategory)}&case_name=${encodeURIComponent(item.file.name)}`,
          {
            method: 'POST',
            headers: authHeaders(),
            body: form,
            signal: ctrl.signal,
          },
        )
        clearTimeout(timeoutId)
        if (!res.ok) {
          const errText = await res.text().catch(() => '')
          let detail = errText
          try { const j = JSON.parse(errText); if (j.detail) detail = j.detail } catch { /* raw */ }
          if (detail.trim().startsWith('<')) detail = lang === 'zh' ? '服务器网关错误，文件可能过大或处理超时，请稍后重试' : 'Gateway error: file may be too large or timed out. Please try again.'
          throw new Error(detail || `HTTP ${res.status}`)
        }
        const data = await res.json()
        setFiles((prev) =>
          prev.map((f, idx) =>
            idx === i
              ? { ...f, status: 'done', precedentId: data.precedent_id, nChunks: data.n_chunks }
              : f,
          ),
        )
        anySuccess = true
      } catch (err: any) {
        clearTimeout(timeoutId)
        const isTimeout = err?.name === 'AbortError'
        const msg = isTimeout
          ? `上传超时（>600s），请检查网络或将大文件拆分后重试`
          : (err?.message || 'Upload failed')
        setFiles((prev) =>
          prev.map((f, idx) =>
            idx === i ? { ...f, status: 'error', error: msg } : f,
          ),
        )
      } finally {
        stopElapsedTimer()
      }
    }

    setUploading(false)
    setUploadingIdx(null)
    if (anySuccess) onUploaded()
  }

  const pendingCount = files.filter((f) => f.status === 'pending' || f.status === 'error').length
  const canUpload = targetCategory && pendingCount > 0 && !uploading

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 mx-4 max-h-[85vh] flex flex-col">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-lg text-brand-400 hover:bg-brand-100 hover:text-brand-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <h3 className="text-lg font-semibold text-brand-800 mb-1">{t.uploadTitle}</h3>
        <p className="text-sm text-brand-400 mb-5">{t.uploadSubtitle}</p>

        {/* 拖拽上传区域 */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragOver(false)
            if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files)
          }}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-6 text-center mb-4 transition-colors cursor-pointer ${
            dragOver
              ? 'border-blue-500 bg-blue-50'
              : 'border-brand-300 hover:border-blue-400 hover:bg-blue-50/30'
          }`}
        >
          <Upload className="w-8 h-8 text-brand-400 mx-auto mb-2" />
          <p className="text-sm text-brand-600 font-medium">{t.dropText}</p>
          <p className="text-xs text-brand-400 mt-1">PDF, DOCX, TXT, MD</p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.docx,.txt,.md,.markdown"
            className="hidden"
            onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = '' }}
          />
        </div>

        {/* 已选文件列表 */}
        {files.length > 0 && (
          <div className="mb-4 max-h-40 overflow-y-auto space-y-1.5">
            {files.map((item, idx) => (
              <div key={idx}>
                <div
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                    item.status === 'done'
                      ? 'bg-green-50 text-green-700'
                      : item.status === 'error'
                        ? 'bg-red-50 text-red-700'
                        : item.status === 'uploading'
                          ? 'bg-blue-50 text-blue-700'
                          : 'bg-brand-50 text-brand-700'
                  }`}
                >
                  {item.status === 'uploading' && <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />}
                  {item.status === 'done' && <CheckCircle className="w-4 h-4 flex-shrink-0" />}
                  {item.status === 'error' && <XCircle className="w-4 h-4 flex-shrink-0" />}
                  {item.status === 'pending' && <FileText className="w-4 h-4 flex-shrink-0" />}
                  <span className="truncate flex-1">{item.file.name}</span>
                  <span className="text-xs opacity-60 flex-shrink-0">
                    {item.status === 'uploading' && uploadingIdx === idx
                      ? t.elapsed(elapsed)
                      : `${(item.file.size / 1024).toFixed(0)} KB`}
                  </span>
                  {item.status === 'done' && item.nChunks != null && (
                    <span className="text-xs text-green-600 flex-shrink-0">{item.nChunks} {t.chunks}</span>
                  )}
                  {(item.status === 'pending' || item.status === 'error') && !uploading && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeFile(idx) }}
                      className="p-0.5 rounded hover:bg-brand-200 transition-colors flex-shrink-0"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {item.status === 'uploading' && uploadingIdx === idx && elapsed >= 10 && (
                  <p className="mt-0.5 ml-8 text-[11px] text-blue-400 leading-snug">
                    {t.timeoutHint}
                  </p>
                )}
                {item.status === 'error' && item.error && (
                  <p className="mt-0.5 ml-8 text-[11px] text-red-500 leading-snug break-words">
                    {item.error}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* 上传至分类 */}
        <div className="mb-5">
          <label className="block text-sm text-brand-700 font-medium mb-2">
            {t.uploadTo}
          </label>
          <select
            value={targetCategory}
            onChange={(e) => setTargetCategory(e.target.value)}
            disabled={uploading}
            className="w-full px-3 py-2.5 rounded-lg border border-brand-200 text-sm text-brand-700 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 bg-white disabled:opacity-50"
          >
            <option value="">{t.selectCategory}</option>
            {flatCats.map((cat) => (
              <option key={cat.key} value={cat.key}>
                {lang === 'zh' ? cat.name_zh : cat.name_en}
              </option>
            ))}
          </select>
        </div>

        {/* 按钮 */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={uploading}
            className="px-4 py-2 rounded-lg border border-brand-300 text-sm text-brand-600 hover:bg-brand-50 transition-colors disabled:opacity-50"
          >
            {t.cancel}
          </button>
          <button
            type="button"
            onClick={handleUpload}
            disabled={!canUpload}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {uploading && <Loader2 className="w-4 h-4 animate-spin" />}
            {t.uploadBtn}
            {files.length > 0 && ` (${files.length})`}
          </button>
        </div>
      </div>
    </div>
  )
}


// ─── 创建分类弹窗 ─────────────────────────────────────────────────────────────
function CreateCategoryModal({
  lang,
  parentKey: initialParent,
  categories,
  onClose,
  onCreated,
}: {
  lang: string
  parentKey: string | null
  categories: KnowledgeCategory[]
  onClose: () => void
  onCreated: (key: string) => void
}) {
  const [nameZh, setNameZh] = useState('')
  const [nameEn, setNameEn] = useState('')
  const [descZh, setDescZh] = useState('')
  const [parentKey, setParentKey] = useState<string>(initialParent ?? '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const parentName = parentKey
    ? (lang === 'zh'
        ? categories.find((c) => c.key === parentKey)?.name_zh
        : categories.find((c) => c.key === parentKey)?.name_en) ?? parentKey
    : null

  const handleCreate = async () => {
    if (!nameZh.trim()) {
      setError(lang === 'zh' ? '请输入分类名称' : 'Category name is required')
      return
    }
    setLoading(true)
    setError('')
    try {
      const res = await fetch('/knowledge/categories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name_zh: nameZh.trim(),
          name_en: nameEn.trim() || nameZh.trim(),
          desc_zh: descZh.trim(),
          desc_en: '',
          parent_key: parentKey || null,
        }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => null)
        setError(data?.detail || (lang === 'zh' ? '创建失败' : 'Failed to create'))
        return
      }
      const data = await res.json()
      onCreated(data.key)
    } catch {
      setError(lang === 'zh' ? '网络错误' : 'Network error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
        <h3 className="text-lg font-semibold text-brand-800 mb-4">
          {parentName
            ? (lang === 'zh' ? `在「${parentName}」下创建子分类` : `Create subcategory under "${parentName}"`)
            : (lang === 'zh' ? '创建新分类' : 'Create New Category')
          }
        </h3>

        {error && (
          <div className="mb-3 p-2.5 rounded-lg bg-red-50 text-sm text-red-600">{error}</div>
        )}

        <div className="space-y-4">
          {/* Parent selector (only show when not pre-set) */}
          {!initialParent && (
            <div>
              <label className="block text-sm font-medium text-brand-700 mb-1">
                {lang === 'zh' ? '上级分类（选填，创建子分类时选择）' : 'Parent Category (optional, for subcategory)'}
              </label>
              <select
                value={parentKey}
                onChange={(e) => setParentKey(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white"
              >
                <option value="">{lang === 'zh' ? '无（创建一级分类）' : 'None (top-level category)'}</option>
                {categories.map((c) => (
                  <option key={c.key} value={c.key}>
                    {lang === 'zh' ? c.name_zh : c.name_en}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-brand-700 mb-1">
              {lang === 'zh' ? '分类名称（中文）' : 'Category Name (Chinese)'} *
            </label>
            <input
              type="text"
              value={nameZh}
              onChange={(e) => setNameZh(e.target.value)}
              placeholder={lang === 'zh' ? '例如：租赁合同纠纷' : 'e.g. Lease Contract Disputes'}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-brand-700 mb-1">
              {lang === 'zh' ? '分类名称（英文，选填）' : 'Category Name (English, optional)'}
            </label>
            <input
              type="text"
              value={nameEn}
              onChange={(e) => setNameEn(e.target.value)}
              placeholder="e.g. Lease Contract Disputes"
              className="w-full px-3 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-brand-700 mb-1">
              {lang === 'zh' ? '分类描述（选填）' : 'Description (optional)'}
            </label>
            <textarea
              value={descZh}
              onChange={(e) => setDescZh(e.target.value)}
              placeholder={lang === 'zh' ? '简要描述该分类包含的知识内容...' : 'Brief description of this category...'}
              rows={2}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-300 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            {lang === 'zh' ? '取消' : 'Cancel'}
          </button>
          <button
            type="button"
            onClick={handleCreate}
            disabled={loading}
            className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60 transition-colors"
          >
            {loading
              ? (lang === 'zh' ? '创建中...' : 'Creating...')
              : (lang === 'zh'
                  ? (parentKey ? '创建子分类' : '创建分类')
                  : (parentKey ? 'Create Subcategory' : 'Create Category'))
            }
          </button>
        </div>
      </div>
    </div>
  )
}

